<template>
  <Teleport to="body">
    <div class="cd-overlay" @click.self="$emit('close')">
      <div class="cd-modal">
        <button class="cd-close" aria-label="Close" @click="$emit('close')">×</button>

        <div class="cd-body">
          <!-- Larger card image -->
          <div class="cd-art">
            <img v-if="card.image_url" :src="card.image_url" :alt="card.name" :class="{ reversed: card.reversed }" />
            <div v-else class="cd-art-ph"></div>
          </div>

          <!-- Name + tabs -->
          <div class="cd-info">
            <span v-if="card.position" class="cd-pos">{{ card.position }}</span>
            <h3 class="cd-name">{{ displayName }}<em v-if="card.reversed"> · reversed</em></h3>
            <span v-if="card.symbolic_name && card.symbolic_name !== card.name" class="cd-traditional">{{ card.name }}</span>

            <div class="cd-tabs">
              <button :class="['cd-tab', { on: tab === 'meaning' }]" @click="tab = 'meaning'">Meaning</button>
              <button v-if="card.card_meaning_reversed" :class="['cd-tab', { on: tab === 'reversed' }]" @click="tab = 'reversed'">Reversed</button>
              <button :class="['cd-tab', { on: tab === 'symbolism' }]" @click="tab = 'symbolism'">Symbolism</button>
            </div>

            <div class="cd-tabpane">
              <p v-if="tab === 'meaning'" class="cd-text">
                {{ card.card_meaning || 'No meaning recorded for this card.' }}
              </p>
              <p v-else-if="tab === 'reversed'" class="cd-text">
                {{ card.card_meaning_reversed || 'No reversed meaning recorded for this card.' }}
              </p>
              <template v-else>
                <div v-if="symbols.length" class="cd-symlist">
                  <div v-for="(s, i) in symbols" :key="i" class="cd-sym">
                    <span v-if="s.symbol" class="cd-sym-name">{{ s.symbol }}</span>
                    <p class="cd-text cd-sym-meaning">{{ s.meaning }}</p>
                  </div>
                </div>
                <p v-else class="cd-text">This reader hasn't authored symbolism for this card yet.</p>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
/**
 * CardDetailModal — layered above the reading: the card at a larger size, with the
 * card's Meaning and this reader's Symbolism as tabs. Opened from a card click in
 * the coven (original) reading.
 */
import { ref, computed } from 'vue'

const props = defineProps({
  card: { type: Object, required: true },  // { name, position, image_url, reversed, card_meaning, reader_symbolism: [{symbol,meaning}] }
})
defineEmits(['close'])

const tab = ref('meaning')
// Lead with the Reader's symbolic name; fall back to the traditional name.
const displayName = computed(() => props.card.symbolic_name || props.card.name)
const symbols = computed(() => Array.isArray(props.card.reader_symbolism) ? props.card.reader_symbolism : [])
</script>

<style scoped>
.cd-overlay { position: fixed; inset: 0; z-index: 9300; display: flex; align-items: center; justify-content: center;
  padding: 24px; background: rgba(10, 0, 21, 0.78); backdrop-filter: blur(3px); }
.cd-modal { position: relative; width: 100%; max-width: 720px; max-height: 88vh; overflow-y: auto;
  background: #160a2b; border: 1px solid rgba(155,134,201,0.4); border-radius: 18px;
  box-shadow: 0 30px 70px -16px rgba(0,0,0,0.7); padding: 28px; }
.cd-close { position: absolute; top: 12px; right: 12px; z-index: 2; width: 32px; height: 32px; border-radius: 50%;
  background: rgba(255,255,255,0.06); color: #c3a9ff; border: none; font-size: 18px; cursor: pointer; }
.cd-close:hover { color: #f0d9a3; }

.cd-body { display: flex; gap: 26px; align-items: flex-start; }
.cd-art { width: 240px; flex: none; border-radius: 12px; overflow: hidden; box-shadow: 0 10px 30px rgba(0,0,0,0.55); }
.cd-art img { width: 100%; display: block; }
.cd-art img.reversed { transform: rotate(180deg); }
.cd-art-ph { width: 240px; height: 384px; background: rgba(93,59,140,0.3); }

.cd-info { flex: 1; min-width: 0; }
.cd-pos { font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 0.09em; text-transform: uppercase; color: rgba(195,169,255,0.6); }
.cd-name { font-family: 'Cinzel', serif; font-size: 24px; color: #FFDFA3; margin: 4px 0 2px; line-height: 1.2; }
.cd-name em { font-style: italic; font-size: 15px; color: rgba(195,169,255,0.75); }
/* The traditional tarot name — small anchor under the Reader's symbolic name. */
.cd-traditional { display: block; font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic;
  font-size: 13px; letter-spacing: 0.03em; color: rgba(195,169,255,0.6); margin: 0 0 16px; }

.cd-tabs { display: flex; gap: 8px; margin-bottom: 14px; border-bottom: 1px solid rgba(93,59,140,0.3); }
.cd-tab { background: none; border: none; border-bottom: 2px solid transparent; color: rgba(195,169,255,0.7);
  font-family: 'Cinzel', serif; font-size: 13px; letter-spacing: 0.04em; padding: 8px 4px; margin-bottom: -1px; cursor: pointer; transition: all 0.18s; }
.cd-tab:hover { color: #f0d9a3; }
.cd-tab.on { color: #FFDFA3; border-bottom-color: #FFDFA3; }

.cd-text { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 18px; line-height: 1.7;
  color: rgba(255,255,255,0.85); margin: 0; white-space: pre-line; }
.cd-symlist { display: flex; flex-direction: column; gap: 14px; }
.cd-sym { display: flex; flex-direction: column; gap: 3px; }
.cd-sym-name { font-family: 'Cinzel', serif; font-size: 13px; letter-spacing: 0.03em; color: #FFDFA3; }
.cd-sym-meaning { font-size: 16px; color: rgba(255,255,255,0.8); }

@media (max-width: 600px) {
  .cd-body { flex-direction: column; align-items: center; text-align: center; }
  .cd-tabs { justify-content: center; }
}
</style>
